"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import Image from "next/image";
import heroMobileBackground from "@/public/global/hero-mobile-background.png";
import { cn } from "@/lib/utils";

export interface IReviewsHeroSectionData {
  heroChip?: string;
  heroTitle?: PortableTextBlock[];
  heroCTA?: GROQButton;
}

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({
  data,
  bgImage,
  mobileBgImageClassName,
}: {
  data: IReviewsHeroSectionData;
  bgImage?: GROQImage;
  mobileBgImageClassName?: string;
}) {
  const resolvedBg = bgImage ?? (data as any).heroBgImage;
  return (
    <section className="relative w-full lg:min-h-[60rem] pt-[9rem] pb-[5.5rem] lg:pt-[14rem] lg:pb-[10rem]">
      {resolvedBg && (
        <div className="pointer-events-none lg:block hidden h-[130rem] absolute w-full left-0 top-0 z-0">
          <GroqImage
            src={resolvedBg.src}
            alt=""
            width={resolvedBg.width}
            height={resolvedBg.height}
            className="!h-full !w-full object-cover object-bottom opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-transparent to-white/80" />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, filter: "blur(8px)" }}
        animate={{ opacity: 30, filter: "blur(0px)" }}
        transition={{ duration: 1.2, ease }}
        className={cn(
          "absolute w-full h-full top-[0rem] lg:hidden block z-0 pointer-events-none",
          mobileBgImageClassName,
        )}
      >
        <Image
          src={heroMobileBackground}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))] flex-col items-center gap-[3.5rem] text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[2.5rem]"
        >
          <div className="flex flex-col items-center gap-[1.25rem]">
            {data.heroChip && (
              <p className="mb-0 font-blacker italic text-[2.25rem] leading-[1.5] text-black/70">
                {data.heroChip}
              </p>
            )}

            {data.heroTitle && (
              <div className="richtext prose-headings:text-black max-w-[101.25rem] flex flex-col gap-[2.5rem]">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data.heroCTA && (
            <div className="w-full lg:w-fit">
              <CTAButton data={data.heroCTA} />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
